import React from 'react';
import clsx from 'clsx';
export const Button = ({ children, onClick, theme, size, disabled, loading = false, loadingText = 'Loading...', icon, isSquare = false, className, fullWidth = false, }) => {
    const handleClick = (e) => {
        e.stopPropagation();
        if (!disabled && !loading) {
            onClick(e);
        }
    };
    const buttonContent = loading ? loadingText : children;
    // Theme-specific button classes
    const themeButtonClasses = {
        primary: 'bg-[#45474b] !text-white/95 hover:bg-[#535356]',
        secondary: 'bg-white text-black/55 hover:bg-[#f9f9f9]',
        text: 'bg-transparent text-[#9e9e9e] shadow-none hover:text-[#636363]',
        accent: 'bg-[#d94545] !text-white', // Using THEME_COLOR_DARK value
        danger: 'bg-[#d8526a] !text-white', // Using NEGATIVE_COLOR value
        flat: 'bg-black/2 text-black/55 hover:bg-black/3 shadow-none',
        special: 'bg-[#6272db] !text-white hover:bg-[#7280dc]', // Purple theme
    };
    // Size classes
    const sizeClasses = {
        small: isSquare
            ? 'rounded-[9px] min-h-[32px] min-w-[32px]'
            : 'px-[14px] py-2 text-[14px] min-h-[32px] min-w-[70px] rounded-[9px]',
        medium: isSquare
            ? 'min-h-[36px] min-w-[36px] rounded-[9px]'
            : 'px-4 text-[14px] min-h-[36px] min-w-[74px] rounded-[9px]',
        large: isSquare
            ? 'px-4 py-4 min-h-[40px] min-w-[40px] rounded-[9px]'
            : 'px-4 text-[14.5px] min-h-[40px] min-w-[80px] rounded-[9px]',
    };
    const wrapperClasses = {
        primary: 'bg-gradient-to-b from-[#616161] to-[#333333]',
        secondary: 'bg-gradient-to-b from-[#e4e4e4] to-[#dedede]',
        accent: 'bg-gradient-to-b from-[#d94545] to-[#d94545]',
        danger: 'bg-gradient-to-b from-[#ea6d83] to-[#c74058]',
        text: 'bg-transparent hover:bg-black/5',
        flat: 'bg-[#efefef] hover:bg-[#e0e0e0]',
        special: 'bg-gradient-to-b from-[#929cdd] to-[#6470c0]', // Purple gradient
    };
    // Icon size classes based on button size
    const iconSizeClasses = {
        small: '!mr-[5.5px]',
        medium: '!mr-1.5',
        large: '!mr-1.5',
    };
    const wrapperSizeClasses = {
        small: 'rounded-[10px] p-[1px]',
        medium: 'rounded-[10px] p-[1px]',
        large: 'rounded-[10px] p-[1px]',
    };
    // Combined button classes
    const buttonClasses = clsx('inline-flex items-center justify-center font-semibold outline-none no-underline border-none', 'transition-all duration-100', size === 'small'
        ? 'shadow-[0px_1px_2px_rgba(0,0,0,0.1)]'
        : 'shadow-[0px_2px_2px_rgba(0,0,0,0.1)]', 'active:shadow-none', themeButtonClasses[theme], sizeClasses[size], (disabled || loading) && 'cursor-not-allowed', !disabled && !loading && 'cursor-pointer', loading && 'cursor-wait', fullWidth && 'w-full');
    // Loading spinner
    const loadingSpinner = (React.createElement("div", { className: clsx('border-2 border-current border-t-transparent rounded-full animate-spin') }));
    // Icon wrapper with proper sizing
    const iconElement = icon && !loading && (React.createElement("div", { className: clsx('flex items-center justify-center', iconSizeClasses[size], 'opacity-90') }, icon));
    // For other themes, render with gradient wrapper
    return (React.createElement("div", { className: clsx('transition-all duration-100 select-none', 'bg-gradient-to-b', wrapperClasses[theme], wrapperSizeClasses[size], 'active:translate-y-[1px]', (disabled || loading) && 'opacity-60', className, fullWidth && 'w-full') },
        React.createElement("button", { onClick: handleClick, disabled: disabled || loading, className: buttonClasses },
            iconElement,
            loading && loadingSpinner,
            buttonContent)));
};
